import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconButton, Skeleton, Stack, Tooltip } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import styles from "./SideBar.module.scss";
import TestImage from "../static/default-profile.png";
import getSideBarData from "./SideBarData";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import { ThemeContext } from "../Contexts/ThemeContext";
import { useSidebarCollapse } from "../Contexts/SidebarCollapseContext";
import { url } from "./url";
import { fetchWithAuth } from "./FetchWithAuth";
import NotificationBell from "../Components/notifications/NotificationBell";
import ThemeSwitcher from "./ThemeSwitcher";
import SidebarOnlineStaff from "./sidebar/SidebarOnlineStaff";
import { canViewOnlineStaffPresence } from "../utils/onlinePresenceAccess";
import { clearAuthSession } from "./clearAuthSession";

function SideBar() {
  const { collapsed, setCollapsed } = useSidebarCollapse();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [details, setDetails] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const api_url = url;
  const { username, setUsername } = useContext(UserContext);
  const { role, setRole } = useContext(RoleContext);
  const { themeMode } = useContext(ThemeContext);

  const showOnlineStaffPresence = canViewOnlineStaffPresence(
    details.role ?? role
  );

  const handleLogout = async () => {
    try {
      await fetchWithAuth(`${api_url}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (error) {
      console.error("Error logging out:", error);
    }
    clearAuthSession({ redirect: false });
    setUsername("");
    setRole("");
    navigate("/");
  };

  const fetchDetails = async () => {
    if (!username) return;
    try {
      const response = await fetchWithAuth(`${api_url}/get_user/${username}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Unauthorized or failed request");
      const data = await response.json();
      setRole(data.role);
      setDetails({
        username: data.username || username,
        role: data.role,
        first_name: data.first_name,
        last_name: data.last_name,
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    if (username) {
      fetchDetails();
    } else {
      handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sideBarData = useMemo(() => getSideBarData(role), [role]);

  const routeIsActive = (routePath = "") => location.pathname === routePath;

  const hasActiveSubItem = (subItems = []) =>
    subItems.some((subItem) => routeIsActive(subItem.link));

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
    setOpenDropdown(null);
    setProfileMenuOpen(false);
  };

  const handleToggleItem = (item, key) => {
    if (item.subItems) {
      if (collapsed) {
        const activeSub = item.subItems.find((sub) => routeIsActive(sub.link));
        const target = activeSub || item.subItems[0];
        if (target?.link) navigate(target.link);
        return;
      }
      setOpenDropdown(openDropdown === key ? null : key);
      return;
    }
    if (item.link) navigate(item.link);
  };

  const displayName =
    [details.first_name, details.last_name].filter(Boolean).join(" ") ||
    details.username ||
    username;

  return (
    <aside
      className={`${styles.Sidebar} ${collapsed ? styles.collapsed : ""}`}
    >
      <Tooltip
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        placement="right"
        arrow
      >
        <button
          type="button"
          className={styles.SidebarToggle}
          onClick={handleToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRightRoundedIcon fontSize="small" />
          ) : (
            <ChevronLeftRoundedIcon fontSize="small" />
          )}
        </button>
      </Tooltip>

      <div className={styles.sidebarContainer}>
        <div className={styles.SidebarHeader}>
          <div className={styles.brandWrap}>
            <div className={styles.brandLogo}>
              <img
                src={TestImage}
                alt="Logo"
                className={styles.brandLogoImage}
              />
            </div>
            <div className={styles.brandText}>
              <h2>CrunchTime</h2>
              <p>Built to get the job done</p>
            </div>
          </div>
        </div>

        <div
          className={`${styles.themeAndNotificationsRow} ${
            collapsed ? styles.rowCollapsed : ""
          }`}
        >
          <div className={styles.headerControlSlot}>
            <ThemeSwitcher collapsed={collapsed} />
          </div>
          <div
            className={`${styles.headerControlSlot} ${styles.notificationBell}`}
          >
            <NotificationBell collapsed={collapsed} />
          </div>
        </div>
      </div>

      <ul className={styles.SidebarList}>
        {sideBarData.map((item, key) => {
          const isItemActive = item.link
            ? routeIsActive(item.link)
            : hasActiveSubItem(item.subItems);

          return (
            <React.Fragment key={key}>
              <li
                className={`${styles.row} ${isItemActive ? styles.active : ""}`}
                onClick={() => handleToggleItem(item, key)}
              >
                <span className={styles.rowInner}>
                  <div className={styles.icon}>{item.icon}</div>
                  <div className={styles.title}>{item.title}</div>
                  {!collapsed && item.subItems && (
                    <div className={styles.menuArrow}>
                      {openDropdown === key ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </div>
                  )}
                </span>
              </li>

              {!collapsed && item.subItems && openDropdown === key && (
                <ul className={styles.SidebarSubList}>
                  {item.subItems.map((sub, subKey) => (
                    <li
                      key={subKey}
                      className={`${styles.row} ${styles.subRow} ${
                        routeIsActive(sub.link) ? styles.active : ""
                      }`}
                      onClick={() => navigate(sub.link)}
                    >
                      <div className={styles.title}>{sub.title}</div>
                    </li>
                  ))}
                </ul>
              )}
            </React.Fragment>
          );
        })}
      </ul>

      <div className={styles.SidebarFooter}>
        {!collapsed && profileMenuOpen && (
          <div className={styles.profileDropdown}>
            <button
              type="button"
              className={`${styles.dropdownItem} ${styles.logoutItem}`}
              onClick={handleLogout}
            >
              <LogoutRoundedIcon />
              <span>Logout</span>
            </button>
          </div>
        )}

        {collapsed ? (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={0.25}
            className={styles.footerActions}
          >
            {showOnlineStaffPresence && (
              <div className={styles.profilePresenceSlotCollapsed}>
                <SidebarOnlineStaff
                  apiUrl={api_url}
                  currentUsername={details.username || username}
                  themeMode={themeMode}
                />
              </div>
            )}
            <Tooltip title="Logout" placement="right">
              <IconButton
                type="button"
                size="small"
                onClick={handleLogout}
                aria-label="Logout"
                sx={{ color: "#fecaca" }}
              >
                <LogoutRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <div className={styles.profileFooterRow}>
            {showOnlineStaffPresence && (
              <div className={styles.profilePresenceSlot}>
                <SidebarOnlineStaff
                  apiUrl={api_url}
                  currentUsername={details.username || username}
                  themeMode={themeMode}
                />
              </div>
            )}
            <button
              type="button"
              className={`${styles.profileSection} ${styles.profileSectionMain} ${
                profileMenuOpen ? styles.profileSectionActive : ""
              }`}
              onClick={() => setProfileMenuOpen((o) => !o)}
            >
              <img
                src={TestImage}
                alt="Profile"
                className={styles.profileAvatar}
              />
              <div className={styles.profileInfo}>
                {displayName ? (
                  <span className={styles.profileName}>{displayName}</span>
                ) : (
                  <Skeleton className={styles.profileName} />
                )}
                {details.role || role ? (
                  <span className={styles.profileRole}>
                    {details.role || role}
                  </span>
                ) : (
                  <Skeleton className={styles.profileRole} />
                )}
              </div>
              <div className={styles.menuArrow}>
                {profileMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </div>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default SideBar;
